import React from 'react'
import {
  Avatar,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  TimePicker,
} from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import {
  useAddDiscountCodeToLeague,
  useUpdateDiscountCode,
} from 'api/league/LeagueDiscount/leagueDiscount'
import {
  formValidated,
  formValidationOnRequiredFields,
  getNameSignature,
} from 'helper/utility'
import moment from 'moment'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import LeagueTeamSelect from './Inputs/LeagueTeamSelect'
import { manageTeamStore } from 'state/league/manageTeam'
import { LeagueDiscountForm } from 'types/league/leagueDiscount'

const { Option } = Select

interface DiscountFormProps {
  form: FormInstance
}

const DiscountForm: React.FC<DiscountFormProps> = ({ form }) => {
  const { leagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
  }))
  const { setAddDiscountModal, selectedDiscount, setSelectedDiscount } =
    manageTeamStore(store => ({
      setAddDiscountModal: store.setAddDiscountModal,
      selectedDiscount: store.selectedDiscount,
      setSelectedDiscount: store.setSelectedDiscount,
    }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)
  const { mutate: addDiscountCode } = useAddDiscountCodeToLeague()
  const { mutate: updateDiscountCode } = useUpdateDiscountCode()

  const organizations = [
    leagueData.organization,
    ...leagueData.guestOrganizations,
  ]

  const formSchema = {
    code: selectedDiscount ? selectedDiscount.code : undefined,
    organizationId: selectedDiscount
      ? selectedDiscount.organizationId
      : undefined,
    type: selectedDiscount ? selectedDiscount.type : undefined,
    discount: selectedDiscount ? selectedDiscount.discount : undefined,
    teamIds: selectedDiscount ? selectedDiscount.teamIds : undefined,
    totalLimit: selectedDiscount ? selectedDiscount.totalLimit : undefined,
    expiry: selectedDiscount ? moment(selectedDiscount.expiry) : undefined,
    ['expiry-time']: selectedDiscount
      ? moment(selectedDiscount.expiry)
      : undefined,
    note: selectedDiscount ? selectedDiscount.note : undefined,
  }

  const selectedOrganization = useWatch('organizationId', form)

  const onFinish = (values: LeagueDiscountForm) => {
    const payload = {
      ...values,
      expiry:
        values.expiry && values['expiry-time']
          ? moment
              .utc(
                `${moment(values.expiry).format('YYYY-MM-DD')} ${moment
                  .utc(values['expiry-time'])
                  .format('HH:mm')}`,
              )
              .format()
          : undefined,
    }

    if (
      !formValidationOnRequiredFields(
        payload,
        [
          'code',
          'organizationId',
          'type',
          'discount',
          'teamIds',
          'totalLimit',
          'expiry',
        ],
        {
          code: "Discount name can't be empty",
          organizationId: 'Organization must be selected',
          type: 'Discount type must be selected',
          discount: "Discount amount can't be empty",
          teamIds: 'Teams must be selected',
          totalLimit: "Quantity can't be empty",
          expiry: "Expiry can't be empty",
        },
      )
    )
      return

    setModalBtnLoader(true)
    if (selectedDiscount) {
      updateDiscountCode(
        {
          discountCodeId: selectedDiscount._id,
          payload,
        },
        {
          onSuccess: () => {
            setSelectedDiscount(null)
            setModalBtnLoader(false)
            setAddDiscountModal(false)
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    } else {
      addDiscountCode(
        {
          leagueId: leagueData._id,
          payload,
        },
        {
          onSuccess: () => {
            setSelectedDiscount(null)
            setModalBtnLoader(false)
            setAddDiscountModal(false)
          },
          onError: () => {
            setModalBtnLoader(false)
            setAddDiscountModal(false)
          },
        },
      )
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={formSchema}
        onFinish={onFinish}
      >
        <Row gutter={10}>
          <Col>
            <label className="input-label required-input">Discount name</label>
          </Col>
          <Col>
            <Form.Item className="form-control" name="code">
              <Input
                placeholder="Discount name"
                className="form-control-field custom-input-field"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={10}>
          <Col>
            <label className="input-label required-input">
              Discount amount
            </label>
          </Col>
          <Col>
            <div className="flex gap-16px">
              <div className="w-80px">
                <Form.Item name="discount">
                  <Input
                    type="number"
                    placeholder="0"
                    className="text-end br-12"
                  />
                </Form.Item>
              </div>
              <div className="w-30p">
                <Form.Item className="pointer selector-input" name="type">
                  <Select
                    className="select-input-field type-select-field"
                    placeholder="Select"
                    optionLabelProp="label"
                    options={[
                      {
                        value: 'percent',
                        label: '% (Percent)',
                      },
                      {
                        value: 'usd',
                        label: '$ (USD)',
                      },
                    ]}
                  />
                </Form.Item>
              </div>
            </div>
          </Col>
        </Row>

        <Row gutter={24} className="">
          <Col span={24}>
            <label htmlFor="" className="input-label required-input">
              Select organization
            </label>
            <Form.Item colon={false} name="organizationId">
              <Select
                placeholder="Select organization"
                className="custom-select-field h-50px"
              >
                {organizations.map(org => {
                  return (
                    <Option name="kidId" value={org._id} key={org._id}>
                      <div className="flex align-center gap-16px">
                        <Avatar src={org.photo?.thumb}>
                          {getNameSignature(org.name)}
                        </Avatar>
                        <p className="text-14 w-400">{org.name}</p>
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {selectedOrganization && (
          <Row gutter={10}>
            <Col>
              <label className="input-label required-input">Team</label>
            </Col>
            <Col>
              <Form.Item className="pointer selector-input" name="teamIds">
                <LeagueTeamSelect
                  form={form}
                  data={selectedDiscount ? selectedDiscount.teamIds : []}
                  leagueId={leagueData._id}
                  organizationId={selectedOrganization}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={10}>
          <Col>
            <label className="input-label required-input">Quantity</label>
          </Col>
          <Col>
            <div className="flex gap-16px">
              <div className="w-80px">
                <Form.Item name="totalLimit">
                  <Input
                    type="number"
                    placeholder="0"
                    className="text-end br-12"
                  />
                </Form.Item>
              </div>
              <p className="mt-12px">people</p>
            </div>
          </Col>
        </Row>

        <Row gutter={20}>
          <Col span={12}>
            <label htmlFor="" className="input-label required-input">
              Expiration date
            </label>
            <Form.Item name="expiry">
              <DatePicker
                className="w-100 form-control-field"
                placeholder="MM/DD/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label htmlFor="" className="input-label required-input">
              Expiration Time
            </label>
            <Form.Item name="expiry-time">
              <TimePicker
                format={'hh:mm a'}
                className="w-100 form-control-field"
                placeholder="HH:MM AM/PM"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label htmlFor="" className="custom-field-label">
              Notes
            </label>
            <Form.Item name="note">
              <Input.TextArea
                className="note-field"
                placeholder="Notes"
                autoSize={{ minRows: 5, maxRows: 10 }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  )
}

export default DiscountForm
